import React from 'react';
import { 
  FaWifi, 
  FaPenNib, 
  FaCamera, 
  FaBatteryFull, 
  FaHdd, 
  FaShieldAlt
} from 'react-icons/fa';
import useScrollAnimation from './useScrollAnimation';

const BentoGrid = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      ref={sectionRef} 
      className={`bento-section ${isVisible ? 'is-visible' : ''}`}
      id="features"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center bento-header scroll-fade-up">
          <span className="section-eyebrow">Key Features</span>
          <h2 className="section-headline">Engineered for Excellence.</h2>
        </div>

        {/* Bento Grid Architecture */}
        <div className="bento-grid">
          
          {/* Card 1: 5G Ultra-Fast & Dual SIM */}
          <div className="bento-card bento-card-large bento-card-chip scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaWifi className="bento-icon" />
              </div>
              <span className="bento-badge">Peace i18 PRO</span>
            </div>
            
            <div className="bento-card-body">
              <h3 className="bento-title">5G Ultra-Fast & Dual SIM</h3>
              <p className="bento-text">
                Blazing network speeds with active dual SIM support for work and life.
              </p>

              <div className="chip-perf-widget">
                <div className="perf-stats-row">
                  <div><strong>5G</strong> Ultra-Fast</div>
                  <div><strong>DUAL SIM</strong> Standby</div>
                  <div><strong>120Hz</strong> FHD+</div>
                </div>
              </div>
            </div>

            <div className="bento-glow-ambient chip-glow" />
          </div>

          {/* Card 2: SC26 Built-in Stylus Pen */}
          <div className="bento-card bento-card-medium bento-card-stylus scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaPenNib className="bento-icon" />
              </div>
              <span className="bento-badge">Peace SC26</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">Built-in Stylus Pen</h3>
              <p className="bento-text">
                Integrated stylus for precision notes, signatures, and sketches.
              </p>
              
              <div className="titanium-preview-box">
                <img 
                  src="/Photo/peace-sc26-poster.jpg" 
                  alt="Peace SC26 Stylus" 
                  className="titanium-thumb"
                />
              </div>
            </div>

            <div className="bento-glow-ambient stylus-glow" />
          </div>

          {/* Card 3: 50MP Rear Camera */}
          <div className="bento-card bento-card-medium bento-card-camera scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaCamera className="bento-icon" />
              </div>
              <span className="bento-badge">Peace NOVA 18 PRO</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">50MP Ultra Clarity</h3>
              <p className="bento-text">
                High-definition 50MP rear camera and 8MP selfie lens for crisp portraits.
              </p>

              <div className="camera-focal-pills">
                <span className="focal-pill active">50MP Main</span>
                <span className="focal-pill">8MP Front</span>
              </div>
            </div>

            <div className="bento-glow-ambient camera-glow" />
          </div>

          {/* Card 4: 5000 mAh Battery */}
          <div className="bento-card bento-card-medium bento-card-battery scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaBatteryFull className="bento-icon" />
              </div>
              <span className="bento-badge">All 3 Models</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">5000 mAh All-Day Power</h3>
              <p className="bento-text">
                Long-lasting endurance built into every device with fast charge support.
              </p>

              <div className="battery-gauge-box">
                <div className="battery-shell">
                  <div className="battery-fluid" />
                  <span className="battery-percentage">5000 mAh</span>
                </div>
              </div>
            </div>

            <div className="bento-glow-ambient battery-glow" />
          </div>

          {/* Card 5: Storage Options */}
          <div className="bento-card bento-card-medium bento-card-display scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaHdd className="bento-icon" />
              </div>
              <span className="bento-badge">Storage Freedom</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">Up to 128GB Storage</h3>
              <p className="bento-text">
                Ample room for all your photos, apps, and documents.
              </p>

              <div className="display-features-row">
                <div className="display-pill">128GB i18 PRO</div>
                <div className="display-pill">Expandable to 128GB</div>
              </div>
            </div>

            <div className="bento-glow-ambient display-glow" />
          </div>

          {/* Card 6: 1-Year Warranty */}
          <div className="bento-card bento-card-medium bento-card-usbc scroll-fade-up">
            <div className="bento-card-header">
              <div className="bento-icon-wrapper">
                <FaShieldAlt className="bento-icon" />
              </div>
              <span className="bento-badge">Certified</span>
            </div>

            <div className="bento-card-body">
              <h3 className="bento-title">1 Year Warranty</h3>
              <p className="bento-text">
                Complete peace of mind with full 1-year coverage on all models.
              </p>

              <div className="usbc-speed-badge">
                <span className="speed-number">1 Year</span>
                <span className="speed-unit">Full Coverage</span>
              </div>
            </div>

            <div className="bento-glow-ambient usbc-glow" />
          </div>

        </div>
      </div>
    </section>
  );
};

export default BentoGrid;
